import React from 'react';

import { DATE_TIME_FORMAT, formatDate } from 'common-utils/date-utils';
import { IAccount } from 'dto/access-management/account-dto';
import { IDisplayDeploymentGroup } from 'dto/deployment/deployment-group-dto';
import { ComponentDto } from 'dto/raptor-component.dto';
import { ComponentVersionDto } from 'dto/raptor-component-version.dto';
import { IStepItem, KeyValueDisplay, Typography, VerticalStepper } from 'ui-lib';
import css from '../../deployment-create.css';
import { useDeviceGroupsStore } from 'store';

interface IRaptorReviewStepProps {
    account: IAccount;
    deploymentGroup: IDisplayDeploymentGroup;
    deploymentName: string;
    selectedComponents: ComponentDto[];
    selectedVersions: Record<string, ComponentVersionDto>;
    selectedScheduleDate?: Date;
}

const safeDisplayString = (displayString?: string) => displayString ?? '-';
export const RaptorReviewStep = (props: IRaptorReviewStepProps): JSX.Element => {
    const deviceGroupStore = useDeviceGroupsStore();
    const deviceGroups = React.useMemo(() => {
        const targetDeviceGroupNames = props.deploymentGroup.targetDeviceGroupNames || [];
        return targetDeviceGroupNames.map((name) => deviceGroupStore.getDeviceGroupByName(name)).filter(Boolean);
    }, [props.deploymentGroup]);

    const deploymentSummary: IStepItem[] = [
        {
            code: '1',
            label: 'General',
            description: (
                <KeyValueDisplay
                    column={1}
                    items={[
                        {
                            title: 'Account',
                            content: safeDisplayString(props.account.name),
                        },
                        {
                            title: 'Deployment group',
                            content: safeDisplayString(props.deploymentGroup.displayLabel),
                        },
                        {
                            title: 'Deployment name',
                            content: safeDisplayString(props.deploymentName),
                        },
                    ]}
                />
            ),
            status: 'finish',
        },
        {
            code: '2',
            label: 'Component versions',
            description: (
                <KeyValueDisplay
                    column={1}
                    items={props.selectedComponents.map((component) => ({
                        title: component.name,
                        content: safeDisplayString(props.selectedVersions[component.uuid]?.version),
                    }))}
                />
            ),
            status: 'finish',
        },
        {
            code: '3',
            label: 'Schedule and targets',
            description: (
                <KeyValueDisplay
                    column={1}
                    items={[
                        {
                            title: 'Scheduled date',
                            content: formatDate(props.selectedScheduleDate, DATE_TIME_FORMAT.DATE_AND_SHORT_TIME),
                        },
                        {
                            title: 'Device groups',
                            content: deviceGroups.length ? (
                                <ul className={css.reviewList}>
                                    {deviceGroups.map((devicegroup, index) => (
                                        <li key={index}>
                                            <Typography.Text>{devicegroup?.deviceGroupLabel}</Typography.Text>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                '-'
                            ),
                        },
                    ]}
                />
            ),
            status: 'finish',
        },
        {
            code: '4',
            label: 'Finish',
            description: 'Ready to create the RAPTOR deployment.',
            status: 'wait',
        },
    ];

    return <VerticalStepper items={deploymentSummary} currentStepCode='4' />;
};
